// Lokasi pembangunan hidup: crane menara berayun pelan (animasi utama), las kecil sesekali, truk molen pelan
// dengan jeda di tepi depan tapak, dan bar progres (persen + sisa waktu) melayang di atas gedung. Progres dari b.konstruksi {mulai_at, selesai_at, lanjut_bid}.
import { TILE_W } from "./iso";
import { paintBuildingAt, refHeight } from "./paint";

export function progresKonstruksi(k, now = Date.now()) {
  if (!k?.mulai_at || !k?.selesai_at) return null;
  const m = new Date(k.mulai_at).getTime(), s = new Date(k.selesai_at).getTime();
  const p = Math.max(0, Math.min(1, (now - m) / Math.max(1, s - m)));
  return { p, sisaMs: Math.max(0, s - now), selesai: now >= s };
}

export function fmtSisa(ms) {
  const mnt = Math.ceil(ms / 60000);
  if (mnt < 60) return `${mnt} mnt`;
  const j = Math.floor(mnt / 60), sisa = mnt % 60;
  return j >= 48 ? `${Math.round(j / 24)} hari` : sisa ? `${j}j ${sisa}m` : `${j} jam`;
}

// Sudut tapak gedung (sadar rotasi) di layar: top/right/bottom/left + pt(u,v,z) interpolasi bilinear,
// dan gpt(u,v) = koordinat grid dunia (untuk kedalaman/oklusi). Dipakai konstruksi.js & aktivitas.js.
export const sudutTapak = (b, def, gridToScreen) => {
  let w = def.w || 1, h = def.h || 1;
  const rot = b.rot || 0;
  if (rot % 2 === 1) [w, h] = [h, w];
  const G = [[b.x, b.y], [b.x + w, b.y], [b.x + w, b.y + h], [b.x, b.y + h]];
  const c = G.map(p => gridToScreen(p[0], p[1]));
  const [top, right, bottom, left] = [0, 1, 2, 3].map(i => c[(i + rot) % 4]);
  const [gt, gr, , gl] = [0, 1, 2, 3].map(i => G[(i + rot) % 4]);
  return {
    top, right, bottom, left, w, h,
    pt: (u, v, z = 0) => [top[0] + u * (right[0] - top[0]) + v * (left[0] - top[0]), top[1] + u * (right[1] - top[1]) + v * (left[1] - top[1]) - z],
    gpt: (u, v) => [gt[0] + u * (gr[0] - gt[0]) + v * (gl[0] - gt[0]), gt[1] + u * (gr[1] - gt[1]) + v * (gl[1] - gt[1])]
  };
};
const sudut = sudutTapak;

// Gerak dengan jeda: tiap siklus `periode` detik, bergerak halus (ease) selama `rasio` bagian lalu diam.
// Keluaran -1..1 bergantian tiap siklus (ayun ke satu sisi, diam, ayun balik, diam).
function ayunJeda(t, periode, rasio = 0.4) {
  const n = Math.floor(t / periode), f = (t / periode - n);
  const e = Math.min(1, f / rasio), ease = e * e * (3 - 2 * e);
  return Math.cos((n + ease) * Math.PI);
}

// Crane menara: tiang kisi di pojok belakang (u=.22,v=.22), lengan utama berayun pelan ±50° lalu berhenti,
// troli geser di sepanjang lengan, kabel + kait naik-turun — semua dengan jeda. Arah lengan dihitung di bidang grid lalu diproyeksi.
function drawCrane(ctx, S, tinggi, T, t, seed, malam) {
  const ex = [(S.right[0] - S.top[0]) / S.w, (S.right[1] - S.top[1]) / S.w];
  const ey = [(S.left[0] - S.top[0]) / S.h, (S.left[1] - S.top[1]) / S.h];
  const [bx, by] = S.pt(0.22, 0.22);
  const tx = bx, ty = by - tinggi;
  const a = 0.6 + seed * 0.5 + ayunJeda(t + seed * 3.1, 16, 0.45) * 0.9;   // sudut lengan: ayun ~7 dtk, diam ~9 dtk
  const L = Math.max(S.w, S.h) * 0.75, Lb = L * 0.35;                   // panjang lengan & lengan penyeimbang (unit grid)
  const dir = (d) => [tx + (Math.cos(a) * ex[0] + Math.sin(a) * ey[0]) * d, ty + (Math.cos(a) * ex[1] + Math.sin(a) * ey[1]) * d];
  const warna = malam ? "#c9a227" : "#f2b224", gelap = malam ? "#4a4a4a" : "#6b6b6b";
  const lw = Math.max(1, 0.025 * T);
  ctx.save(); ctx.lineCap = "round"; ctx.lineJoin = "round";
  // tiang kisi
  ctx.strokeStyle = warna; ctx.lineWidth = lw * 1.4;
  ctx.beginPath(); ctx.moveTo(bx - lw * 1.3, by); ctx.lineTo(tx - lw * 1.3, ty); ctx.moveTo(bx + lw * 1.3, by); ctx.lineTo(tx + lw * 1.3, ty); ctx.stroke();
  ctx.lineWidth = Math.max(0.6, lw * 0.5);
  const seg = Math.max(3, Math.round(tinggi / (0.22 * T)));
  ctx.beginPath();
  for (let i = 0; i < seg; i++) {
    const y1 = by - (tinggi * i) / seg, y2 = by - (tinggi * (i + 1)) / seg;
    ctx.moveTo(bx - lw * 1.3, y1); ctx.lineTo(bx + lw * 1.3, y2);
  }
  ctx.stroke();
  // kabin
  ctx.fillStyle = gelap; ctx.fillRect(tx - lw * 2.2, ty - lw * 2.6, lw * 4.4, lw * 2.6);
  // lengan utama + penyeimbang
  const [jx, jy] = dir(L), [kx, ky] = dir(-Lb);
  ctx.strokeStyle = warna; ctx.lineWidth = lw * 1.2;
  ctx.beginPath(); ctx.moveTo(kx, ky); ctx.lineTo(jx, jy); ctx.stroke();
  // puncak + tali penahan
  const px = tx, py = ty - 0.3 * T;
  ctx.strokeStyle = "rgba(60,60,60,0.9)"; ctx.lineWidth = Math.max(0.6, lw * 0.4);
  ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(jx, jy); ctx.moveTo(px, py); ctx.lineTo(kx, ky); ctx.stroke();
  ctx.strokeStyle = warna; ctx.lineWidth = lw; ctx.beginPath(); ctx.moveTo(tx, ty); ctx.lineTo(px, py); ctx.stroke();
  // beban penyeimbang
  ctx.fillStyle = gelap; ctx.fillRect(kx - lw * 1.6, ky - lw * 0.6, lw * 3.2, lw * 1.8);
  // troli + kabel + kait (jeda berbeda dari lengan agar tak bergerak bersamaan terus)
  const d = L * (0.45 + 0.3 * ayunJeda(t + seed * 2.3 + 5, 11, 0.4));
  const [ux, uy] = dir(d);
  const turun = tinggi * (0.35 + 0.3 * ayunJeda(t + seed * 1.7 + 9, 13, 0.5));
  ctx.fillStyle = gelap; ctx.fillRect(ux - lw * 0.9, uy - lw * 0.5, lw * 1.8, lw * 1.2);
  ctx.strokeStyle = "rgba(40,40,40,0.95)"; ctx.lineWidth = Math.max(0.6, lw * 0.35);
  ctx.beginPath(); ctx.moveTo(ux, uy); ctx.lineTo(ux, uy + turun); ctx.stroke();
  ctx.strokeStyle = gelap; ctx.lineWidth = lw * 0.8;
  ctx.beginPath(); ctx.arc(ux, uy + turun + lw, lw, 0.2, 2.9); ctx.stroke();
  // lampu merah di puncak (kedip pelan, malam lebih terang)
  const kedip = (Math.sin(t * 2) + 1) / 2;
  ctx.fillStyle = `rgba(255,60,60,${malam ? 0.5 + 0.5 * kedip : 0.3 + 0.4 * kedip})`;
  ctx.beginPath(); ctx.arc(px, py, Math.max(1, lw * 0.6), 0, 7); ctx.fill();
  ctx.restore();
}

// Truk molen proyek sebagai item lapisan mover engine (urut kedalaman + oklusi gedung di depannya) —
// sebelumnya dilukis langsung di atas semua sehingga "menembus" gedung depan.
// Pelan, sekali maju-mundur lalu jeda (siklus 0..3.4: 0-1 maju, 1-2 kembali, sisanya pergi).
export function itemTrukKonstruksi(state, defs, gridToScreen, cam, atmos, t, W, H, drawOccluded) {
  const out = [];
  const truk = (defs || []).find(d => d.bid === "truk_molen_semen" && d.paintCode);
  const z = cam.zoom;
  if (!truk || z <= 0.35) return out;
  const byId = {};
  for (const d of defs || []) byId[d.bid] = d;
  for (const b of state.buildings || []) {
    const def = byId[b.bid];
    if (!def?.konstruksi) continue;
    const seed = (b.x * 7 + b.y * 13) % 10;
    const ph = ((t * 0.04 + seed * 0.3) % 3.4);
    if (ph >= 2) continue;
    const S = sudut(b, def, gridToScreen);
    const u = 0.15 + 0.7 * (ph < 1 ? ph : 2 - ph);
    const [sx0, sy0] = S.pt(u, 0.86);
    if (sx0 < -120 || sx0 > W + 120 || sy0 < -120 || sy0 > H + 120) continue;
    const [gx, gy] = S.gpt(u, 0.86), [hx, hy] = S.gpt(u + (ph < 1 ? 0.1 : -0.1), 0.86);
    const wx = hx - gx, wy = hy - gy;
    const dir = Math.abs(wx) >= Math.abs(wy) ? (wx > 0 ? "SE" : "NW") : (wy > 0 ? "SW" : "NE");
    out.push({ d: gx + gy, f: () => drawOccluded(gx, gy, 48 * z, (g, sx, sy) =>
      paintBuildingAt(g, { ...truk, category: "Kendaraan" }, sx, sy, z * 0.9, { time: t, night: atmos.dark, dir })) });
  }
  return out;
}

export function drawKonstruksi(ctx, state, defs, gridToScreen, cam, atmos, t, W, H) {
  const byId = {};
  for (const d of defs || []) byId[d.bid] = d;
  const z = cam.zoom, T = TILE_W * z;
  for (const b of state.buildings || []) {
    const def = byId[b.bid];
    if (!def?.konstruksi) continue;
    const S = sudut(b, def, gridToScreen);
    const xs = [S.top[0], S.right[0], S.bottom[0], S.left[0]], ys = [S.top[1], S.right[1], S.bottom[1], S.left[1]];
    if (Math.max(...xs) < -80 || Math.min(...xs) > W + 80 || Math.max(...ys) < -200 || Math.min(...ys) > H + 80) continue;
    const seed = (b.x * 7 + b.y * 13) % 10;

    // 1) Truk molen → lihat itemTrukKonstruksi (lapisan mover)
    // 2) Crane menara di pojok belakang: lengan berayun pelan, troli geser, kait naik-turun
    if (z > 0.25) drawCrane(ctx, S, refHeight(def.floors ?? 1, S.w, S.h, z) + 0.9 * T, T, t, seed, atmos.dark);
    // 3) Las kecil sesekali: satu titik, redup, jarang menyala
    const nyala = Math.sin(t * 19 + seed) > 0.82 && Math.sin(t * 1.1 + seed * 0.7) > 0.45;
    if (nyala) {
      const [px, py] = S.pt(0.6, 0.4, 0.4 * T);
      ctx.save(); ctx.globalCompositeOperation = "lighter";
      const g = ctx.createRadialGradient(px, py, 0, px, py, 0.06 * T);
      g.addColorStop(0, "rgba(255,255,255,0.8)"); g.addColorStop(0.4, "rgba(160,220,255,0.35)"); g.addColorStop(1, "rgba(120,200,255,0)");
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(px, py, 0.06 * T, 0, 7); ctx.fill();
      ctx.strokeStyle = "rgba(255,230,150,0.7)"; ctx.lineWidth = 1;
      for (let i = 0; i < 3; i++) {
        const a = (t * 40 + i * 2.1) % 6.28, l = (0.02 + ((t * 9 + i) % 1) * 0.04) * T;
        ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px + Math.cos(a) * l, py + Math.sin(a) * l * 0.7 + l * 0.4); ctx.stroke();
      }
      ctx.restore();
    }
    // 3) Bar progres melayang di atas gedung (persen + sisa waktu / "Selesai")
    const pr = progresKonstruksi(b.konstruksi);
    const [cx0, cy0] = S.pt(0.5, 0.5);
    const atas = cy0 - refHeight(def.floors ?? 1, S.w, S.h, z) - 0.55 * T;
    const bw = Math.min(150, Math.max(78, 1.05 * T)), bh = 16 + 4 * Math.min(1, z), r = bh / 2;
    const x0 = cx0 - bw / 2, y0 = atas - bh;
    ctx.save();
    ctx.fillStyle = "rgba(20,20,20,0.72)";
    ctx.beginPath(); ctx.roundRect(x0, y0, bw, bh, r); ctx.fill();
    const label = pr ? (pr.selesai ? "Selesai" : `${Math.round(pr.p * 100)}% · ${fmtSisa(pr.sisaMs)}`) : "Jadwal belum diatur";
    if (pr) {
      ctx.fillStyle = pr.selesai ? "#2ecc71" : "#f2b224";
      ctx.beginPath(); ctx.roundRect(x0 + 2, y0 + 2, Math.max(bh - 4, (bw - 4) * pr.p), bh - 4, r - 2); ctx.fill();
    }
    ctx.fillStyle = "#fff"; ctx.font = `700 ${Math.round(bh * 0.56)}px -apple-system, "Plus Jakarta Sans", sans-serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.shadowColor = "rgba(0,0,0,0.6)"; ctx.shadowBlur = 3;
    ctx.fillText(label, cx0, y0 + bh / 2 + 0.5);
    ctx.restore();
  }
}
